import { ref, onUnmounted } from 'vue';

/**
 * The `usePolling` function is a Vue composition function that enables polling a specified function at
 * a given interval.
 * @param pollFunction - The `pollFunction` parameter is a function that will be called repeatedly at a
 * specified interval for polling data. This function should accept a `signal` parameter, which is an
 * `AbortSignal` object that can be used to abort the polling process if needed. The `pollFunction` can
 * perform any
 * @param [interval=5000] - The `interval` parameter in the `usePolling` function represents the time
 * interval in milliseconds at which the `pollFunction` will be executed repeatedly. By default, if no
 * `interval` value is provided when calling `usePolling`, it will use a default interval of 5000
 * milliseconds
 * @returns The `usePolling` function returns an object with three properties:
 * 1. `start`: a function that starts the polling process.
 * 2. `stop`: a function that stops the polling process.
 * 3. `isPolling`: a ref boolean value that indicates whether the polling process is currently running
 * or not.
 */
export function usePolling(pollFunction, interval = 5000) {
  const isPolling = ref(false);
  let abortController = null;

  const start = async () => {
    // Cancel previous polling if running
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    const signal = abortController.signal;
    isPolling.value = true;

    try {
      while (!signal.aborted) {
        await pollFunction(signal);

        if (!signal.aborted) {
          await new Promise((resolve) =>
            setTimeout(resolve, interval)
          );
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[Polling] Error in polling loop:', err);
      }
    } finally {
      isPolling.value = false;
    }
  };

  const stop = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isPolling.value = false;
  };

  onUnmounted(() => {
    stop();
  });

  return {
    start,
    stop,
    isPolling,
  };
}
